/**
 * Ryan O'Dowd
 * 2024-02-07
 * © Copyright 2024 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  ASSIGNMENT_ROUTE,
  PRACTICE_ROUTE,
  apiSavePublicGoal,
  fetchGoalPreview,
  fetchUserGoalProgress,
  setGoalAssignmentSelectionReviewPercentage,
  setGoalAssignmentSelections,
  toggleMinigameActive,
  updateGoalSettings,
} from '@utils/store/actions';
import {
  Platform,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Accordion from '@components/common/Accordion';
import Assignments from '@containers/Assignment/Assignments';
import Button from '@components/common/Button';
import ButtonGroup from '@components/common/ButtonGroup';
import CPassage from '@classes/Passage';
import Checkbox from '@components/common/Checkbox';
import EmptyScreen from '@components/common/EmptyScreen';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import GoalProgress from '@components/GoalProgress';
import Header from '@components/Header';
import Icon from '@react-native-vector-icons/material-design-icons';
import IconButton from '@components/common/IconButton';
import ListItem from '@components/ListItem';
import Popover from '@components/common/Popover';
import PracticeAndReviewIndicator from './PracticeAndReviewIndicator';
import ProgressIndicator from '@components/ProgressIndicator';
import ScreenLoader from '@components/ScreenLoader';
import ScreenWrapper from '@containers/ScreenWrapper';
import Slider from '@react-native-community/slider';
import Text from '@components/common/Text';
import ToggleCompletedVisibilityButton from '@components/ToggleCompletedVisibilityButton';
import getStyles from './styles';
import hooks from '@hooks';
import minigames from '@utils/globals/minigames';
import utilities from '@utils/utilities';

const Goal = () => {
  const dispatch = hooks.useTypedDispatch();
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  const scrollRef = useRef(undefined);

  const [_isAssignmentSelectionOpen, setIsAssignmentSelectionOpen] = useState(false);
  const [_showSettings, setShowSettings] = useState(false);
  const [_expandAll, setExpandAll] = useState(false);// @TODO: this isn't reactive...e.g. if _expandAll is true, then all modules are collapsed manually, this is still true (web would be useParams from react-router)
  const [_expandedModuleIds, setExpandedModuleIds] = useState([]);

  const activeGoalId = hooks.useTypedSelector((state) => state.settingsActiveGoalId); // @TODO: should this be a prop instead? yes...web and native should both do this // @TODO: this should look at the url param (at least be consistent with it)...and maybe convert the url param to a query param
  const peanuts = hooks.useTypedSelector((state) => state.peanuts);
  const activeGoal = hooks.useTypedSelector((state) => state.goals[activeGoalId]);
  const goalSettings = hooks.useTypedSelector((state) => state.goalSettings);
  const inactiveMinigames = hooks.useTypedSelector((state) => state.inactiveMinigames);
  const fetchGoalsIsLoading = hooks.useTypedSelector((state: any) => state.loadingStates?.fetchGoals);

  const goalAssignmentSelections = hooks.useTypedSelector((state) => state.goalAssignmentSelections[activeGoalId] || [], (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));
  const reviewPercentage = hooks.useTypedSelector((state) => state.goalAssignmentSelectionReviewPercentage);

  const hideCompletedAssignments = hooks.useTypedSelector((state) => !!state.hideCompletedByGoalId[activeGoalId]);

  const user = hooks.useTypedSelector((state) => state.oakUser);
  const lifetimePeanuts = user.lifetime_peanuts;

  const previewGoalId = locationState.previewGoalId;
  const goalPreview = hooks.useTypedSelector((state) => state.goalPreviews[previewGoalId]);

  const goal = previewGoalId ? goalPreview : activeGoal;

  const preferredTranslation = hooks.usePreferredTranslation();

  const userGoalProgress = hooks.useTypedSelector((state) => state.goalProgress[goal?.id]?.[user.id]) || {};

  const hasAlreadyDoneAutoQuickGoal = useRef(Platform.OS === 'web' ? !!window.localStorage.getItem('hasAlreadyDoneAutoQuickGoal') : false); // user should only automatically be forced into a quick goal one time per session; on first goal press, they're taken to a goal right away, but if they navigate back, they shouldn't be kicked back into a lesson

  let errorText = '';
  if (!goalAssignmentSelections.length) {
    errorText = 'Please select at least one passage to work on.'; // @TODO: i18n
  }

  useEffect(() => {
    if (goal && goal.user_goal?.status !== 'active') {
      // need to fetch goal in case it's not in redux (e.g. complete or archived)
      dispatch(fetchUserGoalProgress(goal.id)); // @TODO: do this on app open instead of on goal open?
    }
  }, [goal, dispatch]);

  useEffect(() => {
    if (previewGoalId) {
      dispatch(fetchGoalPreview(previewGoalId, preferredTranslation.id));
    }
  }, [previewGoalId, dispatch, preferredTranslation.id]);

  hooks.useBackButton(() => { // @TODO: can this be part of ScreenWrapper? or something else that is automatically included when needed? like in Header?
    navigation.goBack();
    return true;
  });

  const _goToQuickLesson = useCallback(() => {
    if (!goal?.modules) {
      // @TODO: don't love this solution...this is to prevent a race condition where the ui blocks when tapping wrinkly101 on new install if the goal is undefined, but i don't know why it would be undefined if the name is visible... even if this is a good solution, there should be an alert or something letting the user know it's still loading
      return; // goal is still loading
    }

    const activePassages = goal?.modules.flatMap((m) => m.assignments).filter((a) => a.assignment_type === 'verse_memory').map((a) => CPassage.createFromObject(a.assignment_object.passage)) || [];
    if (!activePassages.length) {
      // @TODO: possible if reading plan with no verse memories?
      return;
    }

    navigation.navigate(PRACTICE_ROUTE, { // @TODO: when we do this on web, Goal unmounts (is replaced), so hitting the back button during Practice will cause _goToQuickLesson to be called again (if there are no peanuts)
      passages: activePassages,
      isTest: false,
    });
  }, [goal, navigation]);

  useEffect(() => {
    if (!hasAlreadyDoneAutoQuickGoal.current && lifetimePeanuts < 1 && goal?.title === 'Wrinkly 101') { // @TODO: don't love hardcoding the name here, but need to prevent group users from having weird ux if they download and go straght to their school/church goal
      hasAlreadyDoneAutoQuickGoal.current = true;
      if (Platform.OS === 'web') {
        window.localStorage.setItem('hasAlreadyDoneAutoQuickGoal', 'true');
      }
      _goToQuickLesson();
    }
  }, [goal, lifetimePeanuts, _goToQuickLesson]);

  const backIcon = <Header.BackIcon />;

  // @TODO: const normalizedScores = useMemo(() => { // @TODO: can't call custom hook in useMemo...but now there are a lot of extra re-renders
  const scores = {};
  let maxScore = 0.0001;
  const passagesToScore = goal?.modules.flatMap((m) => m.assignments).filter((a) => a.assignment_type === 'verse_memory').map((a) => a.assignment_object.passage) || [];
  const passageIdsToAssignmentIds = {};
  goal?.modules.flatMap((m) => m.assignments).filter((a) => a.assignment_type === 'verse_memory').forEach((a) => {
    passageIdsToAssignmentIds[a.assignment_object.passage.id] = a.id;
  });
  const goalPassagesVerses = hooks.useGetVersesForPassages(passagesToScore);
  Object.entries(goalPassagesVerses).forEach(([passageId, verses]: [any, any]) => {
    const numWordsInPassage = verses.join(' ').split(' ').filter((x) => x).length || 0.001; // @TODO: 0.001 is hack for empty passages
    const currScore = (peanuts[passageIdsToAssignmentIds[passageId]] || 0) / numWordsInPassage;
    if (maxScore < currScore) {
      maxScore = currScore;
    }
    scores[passageIdsToAssignmentIds[passageId]] = currScore;
  });

  const normalizedScores = {};
  Object.entries(scores).forEach(([k, v]: [string, number]) => {
    normalizedScores[k] = v ? (v / maxScore) : 0; // @NOTE: 1.1 instead of 1 because if this is the highest score, we want to show a sliver instead of nothing; also, if there are no peanuts earned for this passage, don't show anything yet
  });

  // @TODO: return normScores;
  // @TODO: }, [peanuts, goal?.passages]);

  const goalSettingsItem = (
    <Header.Icon
      icon='cog'
      onPress={() => setShowSettings((curr) => !curr)}
    />
  );
  const selectionItem = (
    <Header.Icon
      icon='playlist-check'
      onPress={() => {
        setIsAssignmentSelectionOpen((curr) => {
          if (goal?.modules.length > 1 && !curr) { // @TODO: need a better way to determine if group goal
            // always expand all on open selection if group goal
            setExpandAll(true);
          }

          return !curr;
        });
      }}
    />
  );
  let rightItems = [goalSettingsItem];
  if (goal?.modules?.some((m) => m.assignments.some((a) => a.assignment_type === 'verse_memory'))) {
    rightItems.unshift(selectionItem);
  }
  if (previewGoalId) {
    rightItems = []; // @TODO: make this an "add goal" button
  }

  const _renderModule = (module) => { // @TODO: move to own component?
    const numCompletedInModule = module.assignments.filter((assignment) => {
      const assignmentProgress = userGoalProgress[assignment.id] || {};
      return assignmentProgress.dc;
    }).length;

    return module.assignments.map((assignment) => {
      const assignmentSelectionObject = goalAssignmentSelections.find((a) => a.id === assignment.id);
      const isActive = assignmentSelectionObject?.status === 'active';
      const isReview = assignmentSelectionObject?.status === 'review';

      const isLocked = new Date(assignment.date_available).getTime() >= Date.now() || new Date(module.date_available).getTime() >= Date.now();
      const isSelectable = ['verse_memory'].includes(assignment.assignment_type);

      const assignmentProgress = userGoalProgress[assignment.id] || {};

      const assignmentPassage = assignment.assignment_object.passage ? CPassage.createFromObject(assignment.assignment_object.passage) : null;

      let checkedStatus: any = isActive ? 'active' : (isReview ? 'review' : 'inactive');
      if (isLocked || !isSelectable) {
        checkedStatus = 'disabled';
      }

      if (hideCompletedAssignments && assignmentProgress.dc && (module.assignments.length !== numCompletedInModule)) {
        return null;
      }

      if (goal.modules.length === 1 && !goal.modules[0].name) { // @TODO: should have better way to determine if memory only than just if there's one module or more than that (it's allowed that a goal has a single module and not all assignments are memory)
        return (
          <View
            key={assignment.id}
            style={styles.passageRow}
          >
            {_isAssignmentSelectionOpen &&
              <TouchableOpacity
                style={styles.checkboxWrapper}
                onPress={() => {
                  if (isActive) {
                    dispatch(setGoalAssignmentSelections(activeGoalId, [...goalAssignmentSelections.filter((a) => a.id !== assignment.id), {...assignment, status: 'review'}]));
                  } else if (isReview) {
                    dispatch(setGoalAssignmentSelections(activeGoalId, goalAssignmentSelections.filter((a) => a.id !== assignment.id)));
                  } else {
                    dispatch(setGoalAssignmentSelections(activeGoalId, [...goalAssignmentSelections, {...assignment, status: 'active'}]));
                  }
                }}
              >
                <PracticeAndReviewIndicator status={checkedStatus} />
              </TouchableOpacity>
            }
            <Assignments.VerseMemory
              {...assignment.assignment_object}
              passage={assignmentPassage}
              progress={assignmentProgress}
              goalId={goal.id}// @TODO: would prefer not to have to pass goalId
              assignmentId={assignment.id}
              normalizedScore={normalizedScores[assignment.id]}
              hideActions={!!previewGoalId}
              isExpanded={_expandAll}
            />
          </View>
        );
      }

      let iconName: any = 'school';
      if (assignment.assignment_type === 'verse_memory' && assignment.assignment_object.must_memorize) {
        iconName = 'test-tube';
      }
      if (assignment.assignment_type === 'reading') {
        iconName = 'book-open-page-variant';
      }
      if (assignment.assignment_type === 'video') {
        iconName = 'video-vintage';
      }
      if (assignment.assignment_type === 'lecture') {
        iconName = 'newspaper-variant-outline'; // @TODO: better icon
      }

      return (
        <View
          key={assignment.id}
          style={styles.checkboxAndItemWrapper}
        >
          {_isAssignmentSelectionOpen &&
            <TouchableOpacity
              style={styles.checkboxWrapper}
              disabled={checkedStatus === 'disabled'}
              onPress={() => {
                if (isActive) {
                  dispatch(setGoalAssignmentSelections(activeGoalId, [...goalAssignmentSelections.filter((a) => a.id !== assignment.id), {...assignment, status: 'review'}]));
                } else if (isReview) {
                  dispatch(setGoalAssignmentSelections(activeGoalId, goalAssignmentSelections.filter((a) => a.id !== assignment.id)));
                } else {
                  dispatch(setGoalAssignmentSelections(activeGoalId, [...goalAssignmentSelections, {...assignment, status: 'active'}]));
                }
              }}
            >
              <PracticeAndReviewIndicator status={checkedStatus} />
            </TouchableOpacity>
          }
          <ListItem
            containerStyles={[styles.assignmentOuterWrapper, isLocked ? styles.disabled : {}]}
            variant={assignmentProgress.dc ? 'accent' : 'primary'}
            disabled={isLocked}
            onPress={() => navigation.navigate(ASSIGNMENT_ROUTE, {
              assignmentId: assignment.id, // just pass the ID instead of the object so that it's reactive (Assignment component will fetch from redux)
              goalId: goal.id,
              hideActions: !!previewGoalId,
            })}
          >
            <View style={styles.iconAndNameWrapper}>
              <Icon style={styles.assignmentTypeIcon} name={iconName} size={20} color={assignmentProgress.dc ? styles.colors.accent : styles.colors.white} />
              {assignmentPassage
                ? <Text style={styles.mainTitle}>{assignmentPassage.prettyPassageName()}</Text>
                : <Text style={styles.mainTitle}>{assignment.name}</Text>
              }
            </View>
            <View style={styles.rowTwo}>
              {!!(assignmentPassage && assignment.name) &&
                <Text style={styles.subTitle}>{assignment.name}</Text>
              }
              {!assignmentProgress.dc && (assignment.date_available || assignment.date_deadline) &&
                <View style={styles.lockedRow}>{/* @TODO: abstract with assignments too */}
                  <Icon name='calendar' size={14} color={styles.colors.accent} />
                  <Text style={styles.availablity}>{utilities.prettyAvailablityDateRange(assignment.date_available, assignment.date_deadline)}</Text>
                </View>
              }
            </View>
          </ListItem>
        </View>
      );
    }).filter((a) => a);
  };

  const userGoals = hooks.useTypedSelector((state) => state.goals);
  const isAlreadyAGoal = !!Object.values(userGoals).find((p: any) => {
    return p.goal_id === previewGoalId;
  });
  const [_goalAdded, setGoalAdded] = useState(false);

  const numAssignmentsCompletedInGoal = goal?.modules.reduce((acc, m) => {
    return acc + m.assignments.reduce((acc2, a) => {
      const assignmentProgress = userGoalProgress[a.id] || {};
      return acc2 + (assignmentProgress.dc ? 1 : 0);
    }, 0);
  }, 0) || 0;

  // @TODO: if goal preview and not loaded yet, show loader; also remove null checks in regular return?
  if (!goal) {
    return (
      <ScreenWrapper>
        <Header
          title={goal?.title}
          titleTextStyle={styles.headerText}// @TODO: what's the purpose of this?
          isMultilineAllowed={true}
          leftItems={[backIcon]}
        />
        {/* @TODO: would be cool if text faded out as it scrolled behind header...small thing but cool ui */}
        <FullScreenScrollView>
          <View style={styles.innerContainer}>
            <ScreenLoader />
          </View>
        </FullScreenScrollView>
      </ScreenWrapper>
    );
  }

  return (
    <ScreenWrapper>
      <Header
        title={goal?.title}
        titleTextStyle={styles.headerText}// @TODO: what's the purpose of this?
        isMultilineAllowed={true}
        leftItems={[backIcon]}
        rightItems={rightItems}
      />
      {/* @TODO: would be cool if text faded out as it scrolled behind header...small thing but cool ui */}
      <FullScreenScrollView>
        {previewGoalId &&
          <View style={styles.subHeaderContainer}>
            <Text style={styles.subHeaderText}>{i18n('previewingGoal')}</Text>
            <Button// @TODO: abstract with button in PublicGoalPreview
              mode='outlined'
              compact={true}
              disabled={isAlreadyAGoal || _goalAdded}
              icon={isAlreadyAGoal ? 'check' : 'plus'}
              theme={isAlreadyAGoal ? styles.colors.primary : styles.colors.primaryExtraDark}
              onPress={() => {
                // @TODO: loading indicator instead of of texts
                setGoalAdded(true);
                dispatch(apiSavePublicGoal(previewGoalId, preferredTranslation.id));
              }}
            >
              {isAlreadyAGoal ? i18n('goalAdded') : i18n('addGoal')}
            </Button>
          </View>
        }
        <View style={styles.innerContainer}>
          {!goal &&
            (fetchGoalsIsLoading
              ? <ScreenLoader />
              : (
                <EmptyScreen.Wrapper>
                  <EmptyScreen.Text>
                    {i18n('selectGoal')}{/* @TODO: this should be automatic */}
                  </EmptyScreen.Text>
                </EmptyScreen.Wrapper>
              )
            )
          }
          <ScrollView
            ref={scrollRef}
            contentContainerStyle={styles.mainListContainer}
            showsVerticalScrollIndicator={false}
          >{/* @TODO: this should be a flatlist instead */}
            {goal &&
              <View style={styles.topDataWrapper}>
                {_showSettings
                  ? (
                    <ListItem
                      variant='primary'
                      disabled={true}
                    >
                      <Text style={styles.title}>Lesson options</Text>{/* @TODO: i18n */}
                      <View style={styles.settingsSectionWrapper}>
                        <Text style={styles.settingsSectionTitle}>{i18n('difficulty')}: </Text>
                        {/* @TODO: sort by ['goal order', 'number of verses/words', 'peanuts', 'wrinkle level'] */}
                        <ButtonGroup
                          options={['easy', 'medium', 'hard'].map((level) => {
                            return {// @TODO: would be nice to theme these, but probably not necessary right now
                              title: utilities.capitalizeFirstLetter(level),
                              value: level,
                            };
                          })}
                          isVertical={false}
                          dense={true}
                          selectedValue={goalSettings.difficulty}
                          onSelect={(newValue) => {
                            dispatch(updateGoalSettings({difficulty: newValue}));
                          }}
                        />
                      </View>
                      <View style={styles.settingsSectionWrapper}>
                        <Text style={styles.settingsSectionTitle}>{i18n('practiceModes')}</Text>{/* @TODO: more creative/fun name than "pracitce modes" */}
                        {minigames(i18n).filter((m) => m.id !== 1).map((game) => {
                          // @TODO: minigames should have attributes that can be checked dynamically here to determine which difficulty levels the game can be played at
                          const isChecked = !inactiveMinigames.includes(game.id);

                          return (
                            <View key={game.id} style={styles.minigameRow}>
                              <Checkbox
                                isChecked={isChecked}
                                color={styles.colors.accent}
                                disabled={false}// @TODO: make automatic based on if this is possible for the current goal and/or selected difficulty (and have alert for user so they understand why it's disabled)
                                onPress={() => dispatch(toggleMinigameActive(game.id))}
                                label={game.name}
                              />
                              <Popover.Wrapper
                                popover={
                                  <Popover.Inner>
                                    <Popover.Text title={true}>{game.name}</Popover.Text>
                                    <Popover.Text>{game.description}</Popover.Text>
                                    {/* @TODO: add graphic here */}
                                  </Popover.Inner>
                                }
                              >
                                <IconButton
                                  style={styles.helpIcon}
                                  iconColor={styles.colors.white}
                                  size={styles.globalScale * 24}
                                  icon='information'
                                />
                              </Popover.Wrapper>
                            </View>
                          );
                        })}
                        {inactiveMinigames.length === minigames(i18n).length &&
                          <Text style={styles.minigameErrorText}>Must select at least one practice mode.</Text>
                        }
                      </View>
                      {/* @TODO: there's too much jumping if this isn't always rendered
                      {goalAssignmentSelections.some((a) => a.status === 'review') && // @TODO: don't love the jumping here when conditionally rendered
                      */}
                      <View style={styles.settingsSectionWrapper}>
                        <View style={styles.reviewTitleWrapper}>
                          <Text style={styles.settingsSectionTitle}>{i18n('reviewQuestions')}</Text>
                          <Popover.Wrapper
                            popover={
                              <Popover.Inner>
                                <Popover.Text title={true}>{i18n('reviewQuestions')}</Popover.Text>
                                <Popover.Text>Percentage of questions that will be review questions.  (Only applies to passages that have been selected for review.  Tap  <Icon name='playlist-check' size={24} /> in the header to select passages.)</Popover.Text>{/* @TODO: i18n */}
                              </Popover.Inner>
                            }
                          >
                            <IconButton
                              style={styles.helpIcon}
                              iconColor={styles.colors.white}
                              size={styles.globalScale * 24}
                              icon='information'
                            />
                          </Popover.Wrapper>
                        </View>
                        <View style={styles.sliderAndTextWrapper}>
                          <Slider
                            style={styles.sliderContainer}
                            value={reviewPercentage}
                            minimumValue={0}
                            minimumTrackTintColor={styles.colors.white}
                            maximumValue={100}
                            maximumTrackTintColor={styles.colors.primaryDark}
                            onSlidingComplete={(value) => dispatch(setGoalAssignmentSelectionReviewPercentage(value))}
                            StepMarker={({stepMarked, currentValue}) => {
                              if (!stepMarked) {
                                return null;
                              }
                              return (
                                <View style={styles.thumbContainer}>
                                  <Text style={styles.thumbText}>{currentValue}%</Text>
                                </View>
                              );
                            }}
                            step={10}
                            thumbTintColor='transparent'
                            tapToSeek={true}
                          />
                        </View>
                      </View>
                      {/* @TODO: quiz question types */}
                      {/* @TODO: review recently missed */}
                      <View style={styles.settingsSectionWrapper}>
                        <Text style={styles.settingsSectionTitle}>Hide completed assignments</Text>{/* @TODO: i18n */}
                        <View style={styles.hideSettingsWrapper}>
                          <Text style={styles.settingsText}>Completed assignments are currently <Text style={styles.settingsTextBold}>{hideCompletedAssignments ? 'hidden' : 'visible'}</Text>.</Text>{/* @TODO: i18n */}
                          <ToggleCompletedVisibilityButton
                            goalId={goal.id}
                            theme={styles.colors.primary}
                            numCompleted={numAssignmentsCompletedInGoal}
                          />
                        </View>
                      </View>
                    </ListItem>
                  )
                  : (
                    <GoalProgress
                      {...goal}
                      hideTitleAndCreationDate={true}
                      hideEdit={!!previewGoalId || (!goal.classroom_goal_metadata.students_can_archive && (new Date(goal.date_deadline).getTime() >= Date.now()))}// @TODO: !goal.classroom_goal_metadata.can_choose_translation
                    />
                  )
                }
              </View>
            }
            <View>
              {_isAssignmentSelectionOpen &&
              <View style={[styles.topRow, goal?.modules.length > 1 && styles.groupGoalTopRow]}>{/* @TODO: better way to determine if there are multiple modules */}
                <TouchableOpacity
                  style={styles.checkboxWrapper}
                  onPress={() => {
                    // @TODO: check if disabled (should never be disabled, but there's a chance that no modules have any assignments that are checkable [i.e. no verse memories])
                    if (goalAssignmentSelections.length === 0) {
                      dispatch(setGoalAssignmentSelections(activeGoalId, goal.modules.flatMap((module) => module.assignments).filter((a) => a.assignment_type === 'verse_memory').map((assignment) => ({...assignment, status: 'active'}))));
                    } else {
                      dispatch(setGoalAssignmentSelections(activeGoalId, []));
                    }
                  }}
                >
                  <PracticeAndReviewIndicator status={goalAssignmentSelections.length === 0 ? 'inactive' : (goalAssignmentSelections.length === (goal.modules.flatMap((module) => module.assignments).length) && goalAssignmentSelections.every((a) => a.status === 'active') ? 'active' : 'indeterminate')} />{/* @TODO: in flatmap...only where assignment_type is memory */}{/* @TODO: can also be disabled */}
                </TouchableOpacity>
                <Popover.Wrapper
                  popover={
                    <Popover.Inner>
                      <Popover.Text title={true}>Selection legend</Popover.Text>
                      <View style={styles.selectionLegend}>{/* @TODO: move this legend into help dialog */}
                        <PracticeAndReviewIndicator status='inactive' showLegend={true} legendTextColor={styles.colors.dialogText} />
                        <PracticeAndReviewIndicator status='active' showLegend={true} legendTextColor={styles.colors.dialogText} />
                        <PracticeAndReviewIndicator status='review' showLegend={true} legendTextColor={styles.colors.dialogText} />
                        <PracticeAndReviewIndicator status='indeterminate' showLegend={true} legendTextColor={styles.colors.dialogText} />
                        <PracticeAndReviewIndicator status='disabled' showLegend={true} legendTextColor={styles.colors.dialogText} />
                      </View>
                    </Popover.Inner>
                  }
                >
                  <IconButton
                    style={styles.helpIcon}
                    iconColor={styles.colors.white}
                    size={styles.globalScale * 24}
                    icon='information'
                  />
                </Popover.Wrapper>
                {/* @TODO: should slider go here instead? */}
                <Text style={styles.errorText}>{errorText}</Text>
                {!errorText &&
                  <Button
                    mode='contained'
                    disabled={!!errorText}
                    theme={styles.colors.accent}
                    compact={true}
                    onPress={() => {
                      navigation.navigate(PRACTICE_ROUTE, {
                        passages: goalAssignmentSelections.filter((a) => a.status === 'active').map((a) => CPassage.createFromObject(a.assignment_object.passage)),
                        reviewPassages: goalAssignmentSelections.filter((a) => a.status === 'review').map((a) => CPassage.createFromObject(a.assignment_object.passage)),
                        reviewPercent: reviewPercentage,
                        isTest: false,
                      });
                    }}
                    icon='school'
                  >
                    Lesson from selection
                  </Button>
                }
                <IconButton
                  style={styles.expandAllButton}
                  iconColor={styles.colors.white}
                  size={styles.globalScale * 22}
                  icon={_expandAll ? 'arrow-collapse' : 'arrow-expand'}
                  onPress={() => setExpandAll((curr) => !curr)}
                />
              </View>

              }
              {(goal.modules.length === 1 && !goal.modules[0].name) // @TODO: should have better way to determine if memory only than just if there's one module or more than that
                ? _renderModule(goal.modules[0])
                : (
                  <View style={styles.modulesWrapper}>
                    {goal.modules.map((module) => {
                      const isLocked = new Date(module.date_available).getTime() >= Date.now();
                      const numCompletedInModule = module.assignments.filter((assignment) => {
                        const assignmentProgress = userGoalProgress[assignment.id] || {};
                        return assignmentProgress.dc;
                      }).length;
                      const isCompleted = module.assignments.length === numCompletedInModule;
                      const numInProgress = module.assignments.filter((assignment) => {
                        const assignmentProgress = userGoalProgress[assignment.id] || {};
                        return assignmentProgress.nlc && !assignmentProgress.dc;
                      }).length;

                      return (
                        <Accordion// @TODO: should this be a ListItem instead?
                          key={module.id}
                          childrenWrapperStyles={styles.module}
                          containerStyles={isCompleted && styles.moduleContainerCompleted}
                          theme={isCompleted ? 'completed' : 'primary'}
                          openIfClosed={_isAssignmentSelectionOpen && _expandAll}
                          closeIfOpen={_isAssignmentSelectionOpen && !_expandAll}
                          disabled={isLocked}
                          title={
                            <View style={styles.moduleNameWrapper}>
                              <View style={styles.moduleProgress}>
                                <ProgressIndicator
                                  completedNum={numCompletedInModule}
                                  inProgressNum={numInProgress}
                                  totalNum={module.assignments.length}
                                  size={24}
                                  width={4}
                                  prefill={100}
                                  hasBadge={false}// @TODO: don't hardcode...due dates? or show progress indicator on module?
                                />
                              </View>
                              <View style={styles.nameAndAvailabliity}>
                                <Text style={styles.moduleName}>{module.name}</Text>
                                {isLocked && !isCompleted && (module.date_available || module.date_deadline) &&
                                  <View style={styles.lockedRow}>{/* @TODO: abstract with assignments too */}
                                    <Icon name='lock' size={14} color={styles.colors.accent} />
                                    <Text style={styles.availablity}>{utilities.prettyAvailablityDateRange(module.date_available, module.date_deadline)}</Text>
                                  </View>
                                }
                              </View>
                            </View>
                          }
                          onChange={(isOpen) => {
                            if (isOpen) {
                              setExpandedModuleIds((curr) => [...curr, module.id]);
                            } else {
                              setExpandedModuleIds((curr) => curr.filter((id) => id !== module.id));
                            }
                          }}
                          rightItem={!previewGoalId && !!numCompletedInModule && _expandedModuleIds.includes(module.id) && (module.assignments.length !== numCompletedInModule) && (
                            <ToggleCompletedVisibilityButton
                              goalId={goal.id}
                              numCompleted={numCompletedInModule}
                              theme={isCompleted ? styles.colors.accent : styles.colors.primary}
                            />
                          )}
                        >
                          {_renderModule(module)}
                        </Accordion>
                      );
                    })}
                  </View>
                )
              }
            </View>
          </ScrollView>
        </View>
      </FullScreenScrollView>
    </ScreenWrapper>
  );
};

export default Goal;
